// 通用的弹窗组件

/**
 * 例子
 * title = 04-26
 * tables []
 * {
 *  showHeader: false,
 *  records: [{
 *    name: 'Sum Of Reviews',
 *    score: '4.1',
 *  }, {
 *    name: 'Selected User Score',
 *    score: '3.9'
 *  }],
 *  columns: [{
 *    key: 'name',
 *    header: 'Name',
 *    render: record => formatMessage({id: record.name})
 *  }]
 * }
 */

import moment from 'moment';
import { moveZero } from '../../pages/community/utils';
import './formatter.scss';
import { ServerTimeType } from '../FilterCommon/types';
import { timeFormatObj } from '../../utils/time';
import { ParamsTimeType } from '../../types/common';
import { reviewsFormat } from '@op/utils/value';
import { channelInfo } from '../ChannelLabel';

/**
 * TODO: 其他系统使用
 * 通用的弹窗组件
 * @param param0
 * @returns
 */
export function formatter({
  title,
  rightTitle,
  footerText,
  tables,
  small = false,
  className = '',
}: {
  className?: string;
  title?: string;
  rightTitle?: string;
  footerText?: string;
  small?: boolean;
  tables: {
    showHeader?: boolean;
    records?: any;
    className?: string;
    columns: {
      key: string;
      align?: string;
      weight?: string;
      header?: string;
      render?: Function;

      headerStyle?: React.CSSProperties;
      bodyStyle?: React.CSSProperties;
      style?: React.CSSProperties; // header 和 style 共用
    }[];
  }[];
}) {
  function renderTables() {
    return tables
      .filter(
        table =>
          Array.isArray(table.records) &&
          table.records.length > 0 &&
          Array.isArray(table.columns) &&
          table.columns.length > 0,
      )
      .map(table => {
        const { className, records, columns, showHeader = true } = table;
        if (records.length === 0) {
          return null;
        }
        let res = `<table class=${className}>`;
        if (showHeader) {
          const headers = columns.map(item => ({
            text: item.header,
            column: item,
          }));
          res += '<thead>';
          res += `
            ${headers
              .map(({ text = '-', column }) => {
                const { align = 'left' } = column;
                return `<th style="text-align:${align};"}>${text}</th>`;
              })
              .join(' ')}
          `;
          res += '</thead>';
        }

        res += '<tbody>';

        res += records
          .map((record: any) => {
            let bodyStr = '<tr>';

            const tds = columns.map((column: any) => {
              const { key, render } = column;
              if (!render)
                return {
                  text: record?.[key] ?? '-',
                  column,
                };
              return {
                text: render(record),
                column,
              };
            });

            bodyStr += `${tds
              .map(({ text = '-', column }) => {
                const { align = 'left', weight = 'normal' } = column;

                return `<td ${
                  weight === 'bold' ? `class="bold"` : ''
                } style="text-align:${align};font-weight:${weight}">${text}</td>`;
              })
              .join(' ')}`;

            bodyStr += '</tr>';

            return bodyStr;
          })
          .join(' ');

        res += '</tbody>';

        res += '</table>';
        return res;
      })
      .join(' ');
  }

  const titleElement = `<div class="chart-common-formatter-header-left">${
    title ? title : ''
  }</div>`;

  const rightTitleElement = `<div class="chart-common-formatter-header-right">${
    rightTitle ? rightTitle : ''
  }</div>`;

  const footerElement = footerText
    ? `<div class="chart-common-formatter-footer">${footerText}</div>`
    : '';

  const element = renderTables();
  if (element) {
    return `
      <div class="chart-common-formatter ${className}" style="${
      small ? 'padding: 10px' : ''
    }">
        ${
          !title && !rightTitle
            ? ''
            : `<div class="chart-common-formatter-header" style="${
                small ? 'margin-bottom: 0' : ''
              }">${titleElement}${rightTitleElement}</div>`
        }

        ${element}
        ${footerElement}
      </div>
    `;
  }

  return;
}

/**
 * TODO: 其他系统使用
 * echart 浮窗小圆点展示
 * @param marker
 * @param seriesName
 * @param className
 * @returns
 */
export const textShowElement = (
  marker: string,
  seriesName: string,
  className?: string,
) =>
  `<span class="chart-common-formatter-marker ${className}">${marker}</span>&nbsp;${seriesName}`;

export const imgShowElement = (img: string, name: string) =>
  `<span class="dbo-chart-formatter-img"><img src=${img} class="chart-common-formatter-img" /> ${name}<span>`;

export const textShowImgElement = (
  marker: string,
  seriesName: string,
  img: string,
  className?: string,
) =>
  `<span class="chart-common-formatter-marker ${className}">${marker}</span>&nbsp;${imgShowElement(
    img,
    seriesName,
  )}`;

/**
 * 渠道展示元素（带图标）
 * 用于 EChart tooltip 中展示渠道信息，自动获取渠道图标
 * @param marker     小圆点标记
 * @param seriesName 渠道名称
 * @param className  自定义样式类名
 * @returns HTML 字符串
 */
export const channelShowElement = (
  marker: string,
  seriesName: string,
  className = 'echart-tooltip',
  isMarketing = false
) => {
  const { img, name } = channelInfo(seriesName as any, undefined, isMarketing);
  return textShowImgElement(marker, name, img, className);
};

type TextShowFunType = (
  marker: string,
  seriesName: string,
  data: any,
  index: number,
) => string;

const valueFormatFun = (val: any) => {
  return isNaN(parseInt(val)) ? '-' : reviewsFormat(val);
};

type RecordsType = Array<{
  text: string;
  value: string;
  originValue: number;
  originText: string;
}>;

type FormatChartParamsDateType = {
  title?: string;
  beforeSort?: boolean;
  rightTitle?: string;
  footerText?: string;
  params: any;
  listTitleFunc?: (item: any) => string;
  addExtraInfo?: (element: any) => {};
  textShowFun?: TextShowFunType;
  valueFormat?: (value: number, element?: any) => any;
  total?: number;
  small?: boolean;
  recordDealwith?: (record: any, element: any) => {};
  dealwithRecords?: (records: RecordsType, total: number) => Array<any> | void;
  tablesConfigFunc?: (records: Array<any>) => Array<any>;
  isSort?: boolean;
  isFilterZero?: boolean;
  className?: string;
};

/**
 * TODO: 其他系统使用
 * EChart浮窗展示组件
 * @param title        左侧标题
 * @param rightTitle   右侧标题
 * @param params           tooltip->formatter 的参数
 * @param listTitleFunc
 *        列表左侧标题，选用字段
 *            参数：    params的每一项
 *            默认值：  seriesName
 * @param textShowFun
 *        左侧标题的展示样式
 *            参数：marker：小圆点
 *                 seriesName： listTitleFunc返回的名称
 *                 data: params的每一项
 * @param valueFormat 右侧数据的展示格式化
 * @param total       总量，内部会自动计算，如果外部传入会覆盖内部计算
 * @param recordDealwith
 *         单条记录处理，可以添加第三个字段或者改变key值
 *             参数：record： 单条记录数据，里面text和value属性
 *                  element： params的每一项，可以在这里找你需要的第三项字段的数据
 *             返回值：返回值会作为records的一条记录
 *
 * @param dealwithRecords
 *         table一列展示数据格式处理
 *             参数：records：当前列表的所有的记录，但是格式都是text，value两个字段
 *                           如果需要添加第三个字段，可以再这里对所有记录进行处理【当然也可以使用recordDealwith方法进行添加】
 *                           这里主要还是为了多添加记录使用
 *                  total：总数，这里方便计算占比，或者添加SUM数据
 *             返回值：
 *                  records为数组，对数组内部直接修改也会影响到展示数据，所以也可以不用返回值
 *                  如果有返回数据，那么覆盖当前数据，作为最后展示数据
 * @param tablesConfigFunc
 *          table基础配置：
 *              参数：records：当前列表的所有的记录
 *              返回值：数组，可以自定义列等操作
 * @returns
 */
export function formatChartParamsDate({
  beforeSort = true,
  isSort = false,
  isFilterZero = false,
  title,
  rightTitle,
  footerText,
  params,
  listTitleFunc = (item: any) => item.seriesName,
  addExtraInfo = (_element: any) => ({}),
  textShowFun = textShowElement,
  valueFormat = valueFormatFun,
  total,
  small = false,
  recordDealwith = (record: { text: string; value: string }) => record,
  dealwithRecords = (records: RecordsType, _total: number) => records,
  tablesConfigFunc = (records: Array<any>) => [
    {
      showHeader: false,
      records,
      columns: [
        {
          key: 'text',
        },
        {
          key: 'value',
          align: 'right',
          weight: 'bold',
        },
      ],
    },
  ],
  className = '',
}: FormatChartParamsDateType) {
  // 展示的值格式
  const sortParams = beforeSort ? moveZero(params) : params; // 默认倒序
  let localTotal: any = 0;
  let records: any = [];
  for (let index = 0; index < sortParams.length; index++) {
    const element = sortParams[index];
    const { value, marker } = element || {};
    const listTitle = listTitleFunc(element);
    const extraInfo = addExtraInfo(element) || {};
    localTotal += value || 0;
    records.push(
      recordDealwith(
        {
          text: textShowFun(marker, listTitle, element, index),
          value: valueFormat(value, element),
          originValue: value || 0,
          originText: listTitle,
          ...extraInfo,
        },
        element,
      ),
    );
  }

  localTotal = total ?? localTotal;
  if (isSort) {
    records.sort(
      (a: { originValue: number }, b: { originValue: number }) =>
        b.originValue - a.originValue,
    );
  }
  if (isFilterZero) {
    records = records.filter(
      (item: { originValue: number }) => item.originValue !== 0,
    );
  }
  const newRecords = dealwithRecords(records, localTotal);
  records = newRecords || records;

  return formatter({
    title,
    small,
    rightTitle,
    footerText,
    className,
    tables: tablesConfigFunc(records),
  });
}

/**
 * TODO: 其他系统使用
 * 日期格式化
 * 例:
 * 日：2022-08-01
 * 时：2022-08-01 12:00
 * 周：2022-08-01 ~ 2022-08-07
 * 月：2023-08
 * @param timeString
 * @param dataType
 * @param isTitle
 * @returns
 */
export const listTimeTitleFunc = (
  timeString: string,
  dataType: ServerTimeType,
  isTitle = true,
) => {
  if (dataType === 'hourly') {
    return moment(timeString).format('YYYY-MM-DD HH:mm');
  }
  if (dataType === 'weekly') {
    if (isTitle) {
      return `${moment(timeString).format('YYYY-MM-DD')} ~ ${moment(timeString)
        .add(6, 'days')
        .format('YYYY-MM-DD')}`;
    }

    return moment(timeString).format('YYYY-MM-DD');
  }
  if (dataType === 'monthly') {
    return moment(timeString).format('YYYY-MM');
  }

  return moment(timeString).format('YYYY-MM-DD');
};

/**
 * echarts的tooltip时间格式化
 * 直接格式化过滤器时间
 * @param {object} filterParams 数值
 * @return 时间字符串
 */
export const echartTooltipTime = (filterParams: any) => {
  const { launched_time, start_time, end_time, date_type } = filterParams;

  // format()里面的字符，需要根据时间粒度来处理
  const star = moment(start_time).format(
    timeFormatObj[date_type as ParamsTimeType],
  );
  const end = moment(end_time).format(
    timeFormatObj[date_type as ParamsTimeType],
  );
  let timeText = launched_time
    ? `Launch ${launched_time === 'all' ? 'to Date' : `${launched_time} Days`}`
    : `${star} ~ ${end}`;

  if (!launched_time && star === end) {
    timeText = star;
  }
  return timeText;
};
